
import React, { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import FilterBar from '../../components/feature/FilterBar';
import ProcurementCard from '../../components/feature/ProcurementCard';
import ProcurementTable from '../../components/feature/ProcurementTable';
import Button from '../../components/base/Button';
import { procurements as mockProcurements } from '../../mocks/procurements';

export default function Procurements() {
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('Kaikki kaupungit');
  const [language, setLanguage] = useState('fi');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  
  const [filters, setFilters] = useState({
    cities: [] as string[],
    categories: [] as string[],
    valueBands: [] as string[],
    dateRange: { start: '', end: '' },
    statuses: [] as string[],
    sortBy: 'updated_desc'
  });

  // Filtered and sorted procurements
  const [filteredProcurements, setFilteredProcurements] = useState(mockProcurements);

  useEffect(() => {
    let filtered = [...mockProcurements];

    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply city filter
    if (selectedCity !== 'Kaikki kaupungit') {
      filtered = filtered.filter(p => p.city === selectedCity);
    }

    // Apply filters
    if (filters.cities.length > 0) {
      filtered = filtered.filter(p => filters.cities.includes(p.city.toLowerCase()));
    }

    if (filters.categories.length > 0) {
      filtered = filtered.filter(p => filters.categories.some(cat => 
        p.category.toLowerCase().includes(cat)
      ));
    }

    if (filters.valueBands.length > 0) {
      filtered = filtered.filter(p => filters.valueBands.includes(p.value_band));
    }

    if (filters.statuses.length > 0) {
      filtered = filtered.filter(p => filters.statuses.includes(p.status.toLowerCase()));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'updated_asc':
          return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        case 'updated_desc':
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        case 'competition_asc':
          return new Date(a.planned_competition_start).getTime() - new Date(b.planned_competition_start).getTime();
        case 'competition_desc':
          return new Date(b.planned_competition_start).getTime() - new Date(a.planned_competition_start).getTime();
        case 'value_asc':
          return a.value_band.localeCompare(b.value_band);
        case 'value_desc':
          return b.value_band.localeCompare(a.value_band);
        case 'title_asc':
          return a.title.localeCompare(b.title);
        case 'title_desc':
          return b.title.localeCompare(a.title);
        case 'city_asc':
          return a.city.localeCompare(b.city);
        case 'city_desc':
          return b.city.localeCompare(a.city);
        default:
          return 0;
      }
    });

    setFilteredProcurements(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, selectedCity, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredProcurements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProcurements = filteredProcurements.slice(startIndex, startIndex + itemsPerPage);

  const handleViewDetails = (id: number) => {
    // Navigate to procurement details
    console.log('View details for procurement:', id);
  };

  const handleFollow = (id: number) => {
    console.log('Follow procurement:', id);
  };

  const handleSaveSearch = () => {
    console.log('Save search with filters:', filters);
  };

  const handleSubscribe = () => {
    console.log('Subscribe to notifications');
  };

  const handleClearFilters = () => {
    setFilters({
      cities: [],
      categories: [],
      valueBands: [],
      dateRange: { start: '', end: '' },
      statuses: [],
      sortBy: 'updated_desc'
    });
    setSearchQuery('');
    setSelectedCity('Kaikki kaupungit');
  };

  const handleSort = (field: string) => {
    setFilters(prev => ({ ...prev, sortBy: field }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Siirry sisältöön
      </a>

      <Header
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCity={selectedCity}
        onCityChange={setSelectedCity}
        language={language}
        onLanguageChange={setLanguage}
      />

      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        onSaveSearch={handleSaveSearch}
        onSubscribe={handleSubscribe}
        onClearFilters={handleClearFilters}
      />

      <main id="main-content" className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Results header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="mb-4 sm:mb-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredProcurements.length} hankintaa löytyi
            </h2>
            <p className="text-sm text-gray-600" aria-live="polite">
              Näytetään {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredProcurements.length)} / {filteredProcurements.length}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* View toggle */}
            <div className="flex bg-white border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1 rounded text-sm transition-colors cursor-pointer ${
                  viewMode === 'cards'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label="Korttinäkymä"
              >
                <i className="ri-layout-grid-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded text-sm transition-colors cursor-pointer ${
                  viewMode === 'table'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label="Taulukkonäkymä"
              >
                <i className="ri-layout-row-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </button>
            </div>

            {/* Items per page */}
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              aria-label="Tulosten määrä sivulla"
            >
              <option value={12}>12 / sivu</option>
              <option value={24}>24 / sivu</option>
              <option value={48}>48 / sivu</option>
            </select>
          </div>
        </div>

        {/* Results */}
        {filteredProcurements.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <i className="ri-search-line text-gray-400 text-6xl mb-4 w-24 h-24 flex items-center justify-center mx-auto" aria-hidden="true"></i>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              Ei osumia valituilla suodattimilla
            </h3>
            <p className="text-gray-600 mb-6">
              Kokeile muuttaa hakuehtoja tai tyhjentää suodattimet löytääksesi hankinnat
            </p>
            <Button onClick={handleClearFilters}>
              Tyhjennä suodattimet
            </Button>
          </div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {paginatedProcurements.map((procurement) => (
                  <ProcurementCard
                    key={procurement.id}
                    procurement={procurement}
                    onViewDetails={handleViewDetails}
                    onFollow={handleFollow}
                  />
                ))}
              </div>
            ) : (
              <div className="mb-8">
                <ProcurementTable
                  procurements={paginatedProcurements}
                  onViewDetails={handleViewDetails}
                  sortBy={filters.sortBy}
                  onSort={handleSort}
                />
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Edellinen
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Seuraava
                  </Button>
                </div>

                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Näytetään <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">
                        {Math.min(startIndex + itemsPerPage, filteredProcurements.length)}
                      </span> / <span className="font-medium">{filteredProcurements.length}</span> tulosta
                    </p>
                  </div>
                  <div>
                    <nav className="flex items-center space-x-2" aria-label="Sivutus">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        aria-label="Edellinen sivu"
                      >
                        <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                      </Button>

                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 text-sm rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            aria-label={`Sivu ${pageNum}`}
                            aria-current={currentPage === pageNum ? 'page' : undefined}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Seuraava sivu"
                      >
                        <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                      </Button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Mobile bottom bar for saved searches and subscriptions */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 sm:hidden z-30">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveSearch} className="flex-1">
              <i className="ri-save-line mr-2 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              Tallenna haku
            </Button>
            <Button variant="outline" size="sm" onClick={handleSubscribe} className="flex-1">
              <i className="ri-notification-line mr-2 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              Tilaa ilmoitukset
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 pb-20 sm:pb-0">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-600">
                © 2025 Hankintakalenteri - Suomen kuntien julkiset hankinnat
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <a href="/tietosuoja" className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer">
                Tietosuoja
              </a>
              <a href="/saavutettavuus" className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer">
                Saavutettavuus
              </a>
              <a href="/ohje" className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer">
                Käyttöohje
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
